<template>
  <ion-modal
    animated
    showBackdrop
    css-class="custom-modal"
    :is-open="isOpened"
    @didDismiss="$emit('onClose')"
  >
    <div class="w-full h-full relative pt-20 gradient-background">
      <div
        class="bg-transparent absolute top-0 left-0 flex items-center justify-between px-8 w-full z-10"
        :style="
          isIos
            ? 'margin-top:35px;height:70px;min-height:70px;'
            : 'height:90px;min-height:90px;'
        "
      >
        <!-- close button (discards temporary changes) -->
        <ion-button
          type="button"
          class="relative my-auto back-button"
          @click="closeFilters"
        >
          <ion-img
            src="/assets/button-icons/close.svg"
            className="pointer-events-none"
          />
        </ion-button>

        <!-- reset button (clears selections, keeps the year) -->
        <ion-button
          type="button"
          fill="clear"
          class="relative my-auto font-helvetica-bold text-16 text-black"
          @click="resetFilters"
        >
          Reset
        </ion-button>
      </div>
      <form class="flex h-full flex-col justify-between" @submit="onSubmit">
        <Title
          titleClass="pb-2 text-black font-helvetica-bold text-28 block px-8"
        >
          Filters
        </Title>
        <div class="flex flex-col px-8" style="flex: 1; overflow-y: scroll">
          <div
            v-for="(option, optKey) in arrayOfOptions"
            :key="optKey"
            class="flex pt-2 pb-1 border-grey border-b w-full justify-between items-center"
          >
            <span
              class="w-1/2 font-helvetica text-16 spacing-5 line-28 text-dark-grey"
              >{{ option.json.span }}</span
            >
            <custom-select
              :name="option.json.name"
              :value="computedFilters[option.json.name]"
              :options="filtersOptions[option.key]"
              :placeholder="option.json.placeholder"
              :disabled="isOptionDisabled(option)"
              @onChange="updateFilters($event, option.json.name)"
            />
          </div>
        </div>
        <div class="w-full px-8 pb-4">
          <big-button type="submit" label="Done" />
        </div>
      </form>
    </div>
  </ion-modal>
</template>
<script>
import Title from "../Title.vue";
import BigButton from "../containers/BigButton.vue";
import { IonImg, IonModal, IonButton } from "@ionic/vue";
import CustomSelect from "../CustomSelect.vue";
import filtersJson from "@/filtersJson";
import { Capacitor } from "@capacitor/core";

// Graphical order of the filters, aligned with the public MedEd calendar
// (segment first, then its dependents, year, month). Used only in live mode.
const FILTERS_ORDER = [
  "segments",
  "segment",
  "categories",
  "category",
  "typology",
  "type_country",
  "country",
  "regions",
  "language",
  "years",
  "year",
  "months",
  "month",
];

export default {
  components: {
    Title,
    BigButton,
    IonButton,
    IonImg,
    IonModal,
    CustomSelect,
  },
  props: {
    filtersState: Object,
    open: Boolean,
    options: Object,
    // When true, the modal drives a server-side progressive fetch:
    // every change is emitted live and picking a segment resets its
    // dependent filters. Used by the Medical Education calendar.
    live: Boolean,
    // Per-screen overrides of filtersJson entries (span, placeholder), by key.
    labels: Object,
  },
  data() {
    return {
      isOpened: false,
      filters: {},
    };
  },
  computed: {
    isIos() {
      if (
        Capacitor &&
        Capacitor.getPlatform() &&
        Capacitor.getPlatform() === "ios"
      ) {
        return true;
      }
      return false;
    },
    computedFilters() {
      return this.filters;
    },
    filtersOptions() {
      return this.options;
    },
    arrayOfOptions() {
      const keys = Object.keys(this.filtersOptions);
      const array = keys.map((key) => {
        if (key === "subcategories") {
          return {};
        }
        const override = this.labels && this.labels[key];
        return {
          key: key,
          json:
            filtersJson[key] && override
              ? { ...filtersJson[key], ...override }
              : filtersJson[key],
        };
      });

      const visible = array.filter(
        (opt) =>
          opt.json &&
          this.filtersOptions[opt.key] &&
          this.filtersOptions[opt.key].length
      );

      if (this.live) {
        // Match the order used on the public MedEd calendar.
        return visible.sort(
          (a, b) => this.filterOrder(a.key) - this.filterOrder(b.key)
        );
      }

      return visible;
    },
  },
  methods: {
    filterOrder(key) {
      const index = FILTERS_ORDER.indexOf(key);
      return index === -1 ? FILTERS_ORDER.length : index;
    },
    isOptionDisabled(option) {
      // In live mode segment is the primary filter: the dependent selects stay
      // locked until the user picks a segment. Segment and year are always
      // usable (year filters the list on its own, with the 2026 default).
      // "All segments" carries value "" and still counts as a pick.
      if (!this.live) return false;
      if (option.json.name === "segment" || option.json.name === "year") {
        return false;
      }
      return this.filters.segment == null;
    },
    updateFilters(value, name) {
      let filterObj = Object.assign({}, this.filters);
      if (this.live && name === "segment" && value == null) {
        // "Select segment" (value null) withdraws the pick: lock the dependents.
        filterObj = { year: this.filters.year };
      } else if (this.live && name === "segment") {
        // Segment is the primary filter: switching it resets the dependents
        // to their "All …" option (value ""), as the public calendar does.
        filterObj = this.withDependentsOnAll({
          year: this.filters.year,
          segment: value,
        });
      } else {
        filterObj[name] = value;
      }
      this.filters = filterObj;
      if (this.live) this.$emit("onChange", this.filters);
    },
    withDependentsOnAll(filterObj) {
      const filled = Object.assign({}, filterObj);
      this.arrayOfOptions.forEach(({ json }) => {
        if (!(json.name in filled)) filled[json.name] = "";
      });
      return filled;
    },
    resetFilters() {
      // Clear every selection; if the screen filters by year, reset it to the
      // current year (not the previously selected one).
      const hasYear = this.filters && this.filters.year != null;
      this.filters = hasYear ? { year: new Date().getFullYear() } : {};
      if (this.live) this.$emit("onChange", this.filters);
    },
    closeFilters() {
      this.$emit("onClose");
    },
    onSubmit(e) {
      e.preventDefault();
      this.$emit("onSubmit", this.filters);
    },
  },
  watch: {
    open: function (newValue) {
      const filterObj = Object.assign({}, this.filtersState);
      this.filters = filterObj;
      this.isOpened = newValue;
    },
    // A new segment can bring filters that were not listed before the refetch:
    // put those on "All …" too instead of leaving them on the placeholder.
    options: function () {
      if (this.live && this.filters.segment != null) {
        this.filters = this.withDependentsOnAll(this.filters);
      }
    },
  },
};
</script>
<style scoped></style>
